import { type ReactElement } from 'react';

import {
  RiBookOpenLine,
  RiBug2Line,
  RiDatabase2Line,
  RiErrorWarningLine,
  RiLightbulbLine,
  RiPriceTag3Line,
  RiShieldCheckLine,
  RiShieldKeyholeLine,
  RiSpeedUpLine,
  RiUserSettingsLine,
} from '@remixicon/react';
import classNames from 'classnames';

import { IssueKind, getIssueKindLabel } from '../../types';
import { IssueIconLabel, type IssueIconLabelSize } from '../IssueIconLabel';

import styles from './IssueKindLabel.module.css';

const IssueKindIcon = ({ kind }: { kind?: IssueKind | null }): ReactElement => {
  switch (kind) {
    case IssueKind.Bug:
      return <RiBug2Line size={16} aria-hidden />;
    case IssueKind.DataQuality:
      return <RiDatabase2Line size={16} aria-hidden />;
    case IssueKind.Documentation:
      return <RiBookOpenLine size={16} aria-hidden />;
    case IssueKind.FeatureRequest:
      return <RiLightbulbLine size={16} aria-hidden />;
    case IssueKind.Performance:
      return <RiSpeedUpLine size={16} aria-hidden />;
    case IssueKind.Reliability:
      return <RiShieldCheckLine size={16} aria-hidden />;
    case IssueKind.Risk:
      return <RiErrorWarningLine size={16} aria-hidden />;
    case IssueKind.Security:
      return <RiShieldKeyholeLine size={16} aria-hidden />;
    case IssueKind.Usability:
      return <RiUserSettingsLine size={16} aria-hidden />;
    default:
      return <RiPriceTag3Line size={16} aria-hidden />;
  }
};

const KIND_CATEGORY_SET_CLASS_NAMES: Record<IssueKind, string> = {
  [IssueKind.Bug]: 'encore-bright-red-set',
  [IssueKind.DataQuality]: 'encore-spearmint-set',
  [IssueKind.Documentation]: 'encore-violet-set',
  [IssueKind.FeatureRequest]: 'encore-royal-blue-set',
  [IssueKind.Performance]: 'encore-gold-set',
  [IssueKind.Reliability]: 'encore-azure-set',
  [IssueKind.Risk]: 'encore-orange-set',
  [IssueKind.Security]: 'encore-fuchsia-set',
  [IssueKind.Usability]: 'encore-lavender-set',
};

export type IssueKindLabelProps = {
  kind?: IssueKind | null;
  size?: IssueIconLabelSize;
};

export const IssueKindLabel = ({
  kind,
  size = 'small',
}: IssueKindLabelProps) => (
  <IssueIconLabel
    icon={<IssueKindIcon kind={kind} />}
    label={kind ? getIssueKindLabel(kind) : 'Unspecified'}
    size={size}
  />
);

export type IssueKindColoredIconProps = {
  kind?: IssueKind | null;
};

export const IssueKindColoredIcon = ({ kind }: IssueKindColoredIconProps) => {
  const label = kind ? getIssueKindLabel(kind) : 'Unspecified kind';

  return (
    <span
      className={classNames(
        styles.coloredIconRoot,
        kind && KIND_CATEGORY_SET_CLASS_NAMES[kind],
      )}
    >
      <span
        className={classNames(styles.coloredIcon, {
          [styles.unspecifiedIcon]: !kind,
        })}
        role="img"
        aria-label={label}
        title={label}
      >
        <IssueKindIcon kind={kind} />
      </span>
    </span>
  );
};
